// PDF export helper — branded DAASMIN Family Office PDFs with logo + table layout.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DAASMIN_LOGO_BASE64 } from "../assets/daasmin-logo-base64";

const BRAND = {
  name: "DAASMIN Family Office",
  tagline: "Consolidated portfolio report",
  primary: [16, 185, 129], // emerald-500
  ink: [15, 23, 42], // slate-900
  muted: [100, 116, 139], // slate-500
};

function headerFooter(doc, { title, subtitle } = {}) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Top band
  doc.setFillColor(...BRAND.ink);
  doc.rect(0, 0, pageW, 24, "F");

  // Logo (left, square 16x16mm)
  try {
    doc.addImage(DAASMIN_LOGO_BASE64, "PNG", 14, 4, 16, 16);
  } catch (e) {
    // If embedding fails we still continue without the logo
  }

  // Brand text
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(BRAND.name, 34, 11);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(180, 200, 220);
  doc.text(subtitle || BRAND.tagline, 34, 17);

  // Date on the right
  const stamp = new Date().toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  doc.setFontSize(8);
  doc.setTextColor(180, 200, 220);
  doc.text(`Generated ${stamp}`, pageW - 14, 17, { align: "right" });

  // Page title (below band)
  if (title) {
    doc.setTextColor(...BRAND.ink);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(title, 14, 34);
  }

  // Footer — thin rule + page number + company
  const page = doc.internal.getCurrentPageInfo().pageNumber;
  const total = doc.internal.getNumberOfPages();
  doc.setDrawColor(220, 225, 232);
  doc.setLineWidth(0.3);
  doc.line(14, pageH - 12, pageW - 14, pageH - 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text("DAASMIN Family Office — Confidential", 14, pageH - 6);
  doc.text(`Page ${page} of ${total}`, pageW - 14, pageH - 6, { align: "right" });
}

/**
 * Build & download a branded PDF.
 * @param {object} cfg
 * @param {string} cfg.title          page title (e.g., "Member holdings")
 * @param {string} [cfg.subtitle]     optional subtitle in the header band
 * @param {string} [cfg.filename]     file name (without extension)
 * @param {string} [cfg.orientation]  "portrait" | "landscape" (default portrait)
 * @param {Array<Array<string>>} [cfg.summary]   small KV block rendered above the table
 * @param {Array<string>} [cfg.columns]          column headers
 * @param {Array<Array<any>>} [cfg.rows]         body rows
 * @param {Array<any>}  [cfg.foot]               footer / totals row
 */
export function exportPDF(cfg) {
  const {
    title = "Report",
    subtitle,
    filename,
    orientation = "portrait",
    summary,
    columns = [],
    rows = [],
    foot,
  } = cfg || {};

  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });

  let cursorY = 42;

  // Summary KV block (optional)
  if (summary && summary.length) {
    autoTable(doc, {
      startY: cursorY,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: { top: 1, right: 4, bottom: 1, left: 0 } },
      columnStyles: {
        0: { textColor: BRAND.muted, fontStyle: "bold" },
        1: { textColor: BRAND.ink },
      },
      body: summary,
    });
    cursorY = doc.lastAutoTable.finalY + 4;
  }

  // Main data table
  if (columns.length || rows.length) {
    autoTable(doc, {
      startY: cursorY,
      head: [columns],
      body: rows,
      foot: foot ? [foot] : undefined,
      styles: {
        fontSize: 8.5,
        cellPadding: 2,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: BRAND.ink,
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      footStyles: {
        fillColor: [241, 245, 249],
        textColor: BRAND.ink,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { top: 28, left: 14, right: 14, bottom: 18 },
      didDrawPage: () => headerFooter(doc, { title, subtitle }),
    });
  } else {
    // No table — still draw header on the single page
    headerFooter(doc, { title, subtitle });
  }

  const today = new Date();
  const stamp = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
  const fname = `${filename || title.replace(/\s+/g, "_").toLowerCase()}_${stamp}.pdf`;
  doc.save(fname);
}
